/**
 * Canonical tag-name matchers for the Labs / Prescriptions filters.
 *
 * Plan 08 (tag-driven section placement): when a user assigns an Inbox
 * document to a chart, its existing tags should drive where it appears in
 * the chart timeline filters. A document tagged "Lab" lands in the Labs
 * filter even when its `document_type` is the legacy `"other"`.
 *
 * The matcher set is intentionally small and English-only for v1. If a
 * clinic uses a tag name we don't recognize, it still works as a normal
 * tag — it just doesn't auto-classify into Labs / Prescriptions.
 */

const LAB_TAG_NAMES: ReadonlySet<string> = new Set([
  'lab',
  'lab_result',
  'laboratory',
  'lab result',
  'lab results',
  'labs',
]);

const RX_TAG_NAMES: ReadonlySet<string> = new Set([
  'medication',
  'medications',
  'meds',
  'prescription',
  'prescriptions',
  'rx',
]);

interface TagLike {
  name: string;
}

export function hasLabTag(tags: readonly TagLike[] | undefined): boolean {
  return !!tags?.some((t) => isLabTag(t.name));
}

export function hasRxTag(tags: readonly TagLike[] | undefined): boolean {
  return !!tags?.some((t) => isRxTag(t.name));
}

export function isLabTag(name: string): boolean {
  return LAB_TAG_NAMES.has(normalize(name));
}

export function isRxTag(name: string): boolean {
  return RX_TAG_NAMES.has(normalize(name));
}

function normalize(name: string): string {
  return name.trim().toLowerCase();
}
